import { faPause, faPlay } from "@fortawesome/pro-solid-svg-icons";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect } from "react";

import LoadingSpinner from "@/common/LoadingSpinner";

import { Icon, IconButton, Typography } from "../..";
import { useVideoLoader } from "../hooks";
import { useVideoEditorState } from "../state";
import type { VideoEditorFile } from "../types";
import { getFrameIndexFromTime } from "../utils/time";
import { VideoScrubber } from "./VideoScrubber";

const MAX_VIDEO_SIZE = 480;

const Container = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2,
  padding: 2,
}));

interface VideoEditorContentProps {
  file: VideoEditorFile;
  onLoadingStateChange?: (isLoading: boolean, error: string | null) => void;
}

export const VideoEditorContent = (props: VideoEditorContentProps) => {
  const { file, onLoadingStateChange } = props;
  const {
    currentTime,
    setCurrentTime,
    duration,
    setDuration,
    isPlaying,
    play,
    pause,
    frames,
    setFrames,
    setCurrentFrameIndex,
  } = useVideoEditorState();

  const { videoRef, canvasRef, isLoading, error } = useVideoLoader({
    videoSrc: file.videoSrc || "",
    onDurationSet: setDuration,
    onFramesSet: setFrames,
  });

  // Notify parent component of loading state changes
  useEffect(() => {
    onLoadingStateChange?.(isLoading, error);
  }, [isLoading, error, onLoadingStateChange]);

  const handleScrubberTimeChange = (time: number) => {
    setCurrentTime(time);

    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }

    // Find the closest frame
    if (duration > 0 && frames.length > 0) {
      const frameIndex = getFrameIndexFromTime(time, duration, frames.length);
      setCurrentFrameIndex(
        Math.max(0, Math.min(frameIndex, frames.length - 1)),
      );
    }
  };

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    if (isLoading) return;

    const video = e.target as HTMLVideoElement;
    setCurrentTime(video.currentTime);
  };

  const handlePlayPause = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      pause();
      videoRef.current.pause();
    } else {
      play();
      videoRef.current.play();
    }
  };

  if (error) {
    return (
      <Box
        sx={{ height: "100%", width: "100%", padding: 2, textAlign: "center" }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Container>
      <Box
        sx={{
          position: "relative",
          width: MAX_VIDEO_SIZE,
          height: MAX_VIDEO_SIZE,
          marginY: 8,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          borderRadius: 1,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: 48,
            }}
          >
            <LoadingSpinner />
          </Box>
        ) : null}
        <video
          ref={videoRef}
          src={file.videoSrc}
          crossOrigin="anonymous"
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            display: isLoading ? "none" : "block",
          }}
          onTimeUpdate={handleTimeUpdate}
          onPlay={play}
          onPause={pause}
        />
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </Box>
      <Box sx={{ width: "100%", maxWidth: MAX_VIDEO_SIZE }}>
        <VideoScrubber
          frames={frames}
          currentTime={currentTime}
          duration={duration}
          onTimeChange={handleScrubberTimeChange}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            mt: 2,
            mb: 4,
          }}
        >
          <IconButton
            size="large"
            onClick={handlePlayPause}
            disabled={isLoading}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            <Icon icon={isPlaying ? faPause : faPlay} size="large" />
          </IconButton>
        </Box>
      </Box>
    </Container>
  );
};
